import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Trans, useTranslation } from 'react-i18next';
import OptimusButton from '../../../../common/library/Button/Button';
import { TestIds } from '../../../../assets/testIds';
import { getCanonicalPartnerName } from '../../../../common/constants';
import { toSlug } from '../../../../common/utils/urlUtils';

type ImInterestedDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  partnerName: string;
  partnerCode?: string;
};

export function ImInterestedDialog({
  open,
  onClose,
  onConfirm,
  partnerName,
  partnerCode,
}: ImInterestedDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  // Rename-stable name for deriving a11y ids (see getCanonicalPartnerName).
  const partnerKeyName = getCanonicalPartnerName(partnerCode, partnerName);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={`${toSlug(partnerKeyName)}-im-interested-dialog-title`}
      aria-describedby={`${toSlug(partnerKeyName)}-im-interested-dialog`}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: theme.spacing(84),
          borderRadius: theme.spacing(1.5),
        },
      }}
    >
      <DialogContent sx={{ padding: theme.spacing(3) }}>
        <IconButton
          aria-label={t('button.close', { defaultValue: 'Close' }) as string}
          onClick={onClose}
          data-testid={TestIds.partnerDetailsPage.imInterestedDialogCloseBtn}
          sx={{
            position: 'absolute',
            right: theme.spacing(2),
            top: theme.spacing(2),
            color: theme.palette.grey[700],
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          <Typography
            variant="h4"
            id={`${toSlug(partnerKeyName)}-im-interested-dialog-title`}
            sx={{ paddingRight: theme.spacing(4) }}
          >
            {t('partnerDetails.imInterested.title')}
          </Typography>
          <Typography
            variant="body8"
            color={theme.palette.grey[700]}
            id={`${toSlug(partnerKeyName)}-im-interested-dialog`}
          >
            <Trans
              i18nKey="partnerDetails.imInterested.description"
              t={t}
              // The display name is shown here, not the rename-stable analytics key.
              values={{ partnerName }}
              components={{
                boldTag: (
                  <Typography
                    component="span"
                    variant="body8"
                    sx={{ fontWeight: 700 }}
                  />
                ),
              }}
            />
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <OptimusButton
              customVariant="primary"
              size="m"
              fullWidth={false}
              onClick={onConfirm}
              buttonText={t('button.confirm', { defaultValue: 'Confirm' }) as string}
              data-testid={TestIds.partnerDetailsPage.imInterestedConfirmBtn}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
